import { setDoc, updateDoc, arrayUnion, getDoc, serverTimestamp } from 'firebase/firestore';
import { useAgency } from '../AgencyContext';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase.config';

export const useTaskTracker = () => {
  const { getAgencyDoc } = useAgency();
  const [user] = useAuthState(auth);

  const calculateAggregatedStats = (events, isVideo = false, taskQuantity = 0) => {
    const stats = {
      L1username: null,
      L1acceptedimages: 0,
      L1rejectedimages: 0,
      L2username: null,
      L2acceptedimages: 0,
      L2rejectedimages: 0,
      L2acceptedvideos: 0,
      L2rejectedvideos: 0,
      QAusername: null,
      QAacceptedimagesL1: 0,
      QArejectedimagesL1: 0,
      QAacceptedimagesL2: 0,
      QArejectedimagesL2: 0,
      QAacceptedvideosL2: 0,
      QArejectedvideosL2: 0
    };

    if (!events || events.length === 0) return stats;

    // Get usernames
    const l1User = events.find(e => e.userRole === 'L1')?.userId;
    const l2User = events.find(e => e.userRole === 'L2')?.userId;
    const qaUser = events.find(e => e.userRole === 'QA')?.userId;

    stats.L1username = l1User || null;
    stats.L2username = l2User || null;
    stats.QAusername = qaUser || null;

    // Count L1/L2 phase completions
    const l1Completed = events.some(e => e.action === 'Validated task L1');
    const l2Completed = events.some(e => e.action === 'Validated task L2');

    if (l1Completed) {
      // L1 always works on images
      const quantity = taskQuantity || 
                      parseInt(events.find(e => e.action.includes('uploaded') && e.userRole === 'L1')?.action.match(/\d+/)?.[0]) || 
                      0;
      stats.L1acceptedimages = quantity;
    }

    if (l2Completed) {
      const quantity = taskQuantity || 
                      parseInt(events.find(e => e.action.includes('uploaded') && e.userRole === 'L2')?.action.match(/\d+/)?.[0]) || 
                      0;
      if (isVideo) {
        stats.L2acceptedvideos = quantity;
      } else {
        stats.L2acceptedimages = quantity;
      }
    }

    // Count QA decisions and adjust L1/L2 rejected counts
    const l1ValidationTime = events.find(e => e.action === 'Validated task L1')?.timestamp;
    
    events.forEach(event => {
      if (event.userRole === 'QA' && 
          (event.action.includes('Accepted') || event.action.includes('Rejected') || 
           event.action.includes('Changed decision to Accepted') || event.action.includes('Changed decision to Rejected'))) {
        
        const isAccepted = event.action.includes('Accepted') || event.action.includes('Changed decision to Accepted');
        const isRejected = event.action.includes('Rejected') || event.action.includes('Changed decision to Rejected');
        const isL2Phase = event.action.includes('L2') || 
                         (l1ValidationTime && new Date(event.timestamp) > new Date(l1ValidationTime));

        if (isL2Phase) {
          // L2 phase QA decisions - detect content type based on what L2 actually processed
          // Check if L2 processed videos by looking at L2 completion stats  
          const l2ProcessedVideos = stats.L2acceptedvideos > 0 || events.some(e => 
            e.userRole === 'L2' && (e.action.includes('uploaded') && e.action.includes('video'))
          );
          
          if (isVideo || l2ProcessedVideos) {
            if (isAccepted) {
              stats.QAacceptedvideosL2++;
            } else if (isRejected) {
              stats.QArejectedvideosL2++;
              // Add to L2 rejected videos count
              stats.L2rejectedvideos++;
            }
          } else {
            if (isAccepted) {
              stats.QAacceptedimagesL2++;
            } else if (isRejected) {
              stats.QArejectedimagesL2++;
              // Add to L2 rejected images count
              stats.L2rejectedimages++;
            }
          }
        } else {
          // L1 phase QA decisions (always images)
          if (isAccepted) {
            stats.QAacceptedimagesL1++;
          } else if (isRejected) {
            stats.QArejectedimagesL1++;
            // Add to L1 rejected images count
            stats.L1rejectedimages++;
          }
        }
      }
    });

    return stats;
  };

  const trackEvent = async (taskId, taskName, action, userRole = null, taskQuantity = null, metadata = null, isVideo = null, hasL2Process = null, profileName = null, createdBy = null) => {
    if (!user || !taskId || !taskName || !action) {
      console.warn('Missing required data for tracking:', { user: !!user, taskId, taskName, action });
      return;
    }

    try {
      const trackingDocRef = getAgencyDoc('tasktracking', taskId);
      if (!trackingDocRef) {
        console.error('Could not get agency document reference for task tracking');
        return;
      }

      const event = {
        timestamp: new Date().toISOString(),
        userId: user.email,
        userRole: userRole || 'Unknown',
        action: action,
        taskName: taskName,
        taskId: taskId,
        ...(metadata && { metadata })
      };

      // Check if document exists
      const docSnap = await getDoc(trackingDocRef);
      let currentEvents = [];
      let currentIsVideo = isVideo;
      
      if (docSnap.exists()) {
        currentEvents = docSnap.data().events || [];
        currentIsVideo = docSnap.data().isVideo;
      }

      // Add new event to the events array
      const updatedEvents = [...currentEvents, event];
      
      // Calculate aggregated stats
      const currentQuantity = taskQuantity || (docSnap.exists() ? docSnap.data().taskQuantity : 0);
      const aggregatedStats = calculateAggregatedStats(updatedEvents, currentIsVideo, currentQuantity);
      
      if (docSnap.exists()) {
        // Document exists, add event to array and update stats
        await updateDoc(trackingDocRef, {
          events: arrayUnion(event),
          taskQuantity: taskQuantity || docSnap.data().taskQuantity,
          lastUpdated: serverTimestamp(),
          ...aggregatedStats
        });
      } else {
        // Document doesn't exist, create it
        await setDoc(trackingDocRef, {
          taskId: taskId,
          taskName: taskName,
          taskQuantity: taskQuantity,
          isVideo: isVideo,
          hasL2Process: hasL2Process,
          profileName: profileName,
          createdBy: createdBy,
          events: [event],
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          ...aggregatedStats
        });
      }

      console.log('Task tracking event recorded:', event);
    } catch (error) {
      console.error('Error recording task tracking event:', error);
    }
  };

  const updateEvent = async (taskId, taskName, imageId, newAction, userRole = null, taskQuantity = null, metadata = null) => {
    if (!user || !taskId || !taskName || !newAction || !imageId) {
      console.warn('Missing required data for event update:', { user: !!user, taskId, taskName, newAction, imageId });
      return;
    }

    try {
      const trackingDocRef = getAgencyDoc('tasktracking', taskId);
      if (!trackingDocRef) {
        console.error('Could not get agency document reference for event update');
        return;
      }

      // Get current document
      const docSnap = await getDoc(trackingDocRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const events = data.events || [];
        
        // Find existing event for this image by this user
        const eventIndex = events.findIndex(event => 
          event.userId === user.email && 
          (event.metadata?.imageId === imageId || event.metadata?.imageUrl?.includes(imageId))
        );
        
        if (eventIndex !== -1) {
          // Update existing event
          const updatedEvent = {
            ...events[eventIndex],
            action: newAction,
            timestamp: new Date().toISOString(),
            userRole: userRole || events[eventIndex].userRole
          };
          
          // Handle metadata update - remove comment if changing to Accepted
          if (metadata) {
            updatedEvent.metadata = { ...events[eventIndex].metadata, ...metadata };
            
            // Remove comment if changing to Accepted
            if (newAction === 'Changed decision to Accepted' && updatedEvent.metadata.comment) {
              delete updatedEvent.metadata.comment;
            }
          }
          
          events[eventIndex] = updatedEvent;
          
          // Recalculate aggregated stats after event update
          const docData = docSnap.data();
          const aggregatedStats = calculateAggregatedStats(events, docData.isVideo, docData.taskQuantity);
          
          await updateDoc(trackingDocRef, {
            events: events,
            lastUpdated: serverTimestamp(),
            ...aggregatedStats
          });
          
          console.log('Task tracking event updated:', events[eventIndex]);
          return true; // Event was updated
        }
      }
      
      return false; // Event was not found, need to create new one
    } catch (error) {
      console.error('Error updating task tracking event:', error);
      return false;
    }
  };

  return {
    trackEvent,
    updateEvent
  };
};